import type { BasePageProps } from '@shared/components/BasePageProps';
import type { SnackBarConfig } from '@shared/components/Snackbar';
import { Close, Insights } from '@mui/icons-material';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import React, { useState } from 'react';
import SEO from '@/shared/components/SEO';
import CustomSnackbar from '@shared/components/Snackbar';
import { DealInsightStep } from '../welcome/components/onboarding-wizard/steps/DealInsightStep';
import ReviewSection from '../welcome/components/ReviewSection';
import { HourlyRateBanner } from './components/HourlyRateBanner';
import './styles/my-reviews.scss';

interface MyReviewsPageProps extends BasePageProps {
  embedded: boolean;
}

const SEO_TITLE_EMBEDDED = 'My Deal Retros';
const SEO_TITLE_STANDALONE = 'My Deal Retros - Contributor Dashboard';
const SEO_DESCRIPTION
  = 'View and manage all the company deal retros you\'ve contributed to '
    + 'the Kandir platform. Track your contributions and their impact.';

const MyReviewsPage = (props: MyReviewsPageProps): React.ReactElement => {
  const { embedded } = props;
  const [showDealInsightsModal, setShowDealInsightsModal] = useState(false);
  const [snackbarConfig, setSnackbarConfig] = useState<SnackBarConfig>();

  const handleSnackBarClose = (value: boolean): void => {
    setSnackbarConfig({
      ...snackbarConfig,
      open: value,
    });
  };

  return (
    <>
      <SEO
        title={embedded ? SEO_TITLE_EMBEDDED : SEO_TITLE_STANDALONE}
        description={SEO_DESCRIPTION}
        ogImage="https://app.kandirnetwork.io/kandir_logo.png"
        twitterImage="https://app.kandirnetwork.io/kandir_logo.png"
      />

      <div
        className={
          embedded
            ? 'my-reviews-page my-reviews-page--embedded'
            : 'my-reviews-page'
        }
        data-testid="my-reviews-page"
      >
        {!embedded && (
          <header className="my-reviews-page__header">
            <div className="my-reviews-page__title-row">
              <div className="my-reviews-page__title-block">
                <p className="my-reviews-page__eyebrow">
                  Contributor Dashboard
                </p>
                <h1 className="my-reviews-page__title">My Deal Retros</h1>
                <p className="my-reviews-page__subtitle">
                  View and track every deal retro you've contributed to
                  Kandir, and update your deal history in a few clicks.
                </p>
              </div>

              <div className="my-reviews-page__action">
                <button
                  type="button"
                  className="my-reviews-page__action-btn"
                  onClick={() => setShowDealInsightsModal(true)}
                  data-testid="my-reviews-add-quick-retro"
                >
                  <Insights fontSize="small" />
                  Update Your Deal History
                </button>
                <p className="my-reviews-page__action-hint">
                  Quickly list your deal experience
                </p>
              </div>
            </div>
          </header>
        )}

        <HourlyRateBanner />
        <ReviewSection hideFilters={false} parentPageName="my-reviews" />
      </div>

      <Dialog
        open={showDealInsightsModal}
        onClose={() => setShowDealInsightsModal(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ className: 'my-reviews-page__modal-paper' }}
      >
        <DialogTitle className="my-reviews-page__modal-title">
          <span className="my-reviews-page__modal-title-lede">
            <Insights fontSize="small" />
            <span className="my-reviews-page__modal-title-heading">
              Add Quick Retro
            </span>
          </span>
          <IconButton
            onClick={() => setShowDealInsightsModal(false)}
            size="small"
            aria-label="Close add quick retro dialog"
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DealInsightStep
            onNext={() => {
              setShowDealInsightsModal(false);
              setSnackbarConfig({
                open: true,
                type: 'success',
                message: 'Deal insights saved successfully!',
              });
            }}
            onSkip={() => setShowDealInsightsModal(false)}
          />
        </DialogContent>
      </Dialog>

      <CustomSnackbar config={snackbarConfig} setOpen={handleSnackBarClose} />
    </>
  );
};

export default MyReviewsPage;
